import Configuration from '@/agilis/util/Configuration'

const example = `{
  "id": "propositionFsm",
  "states":
  [
    { "id": "S1", "label": "S1" },
    { "id": "S2", "label": "S2" },
    { "id": "S3", "label": "S3" }
  ],
  "events":
  [
    { "id": "GotoS1", "label": "Go to S1" },
    { "id": "GotoS2", "label": "Go to S2" },
    { "id": "GotoS3", "label": "Go to S3" }
  ],
  "actions":
  [
    { "id": "GotoS1", "label": "Go to S1" },
    { "id": "GotoS2", "label": "Go to S2" },
    { "id": "GotoS3", "label": "Go to S3" }
  ],
  "transitions":
  [
    { "id": "t100", "from": "S1", "event": "GotoS2", "to": "S2", "action": "GotoS2" },
    { "id": "t101", "from": "S1", "event": "GotoS3", "to": "S3", "action": "GotoS3" },
    { "id": "t204", "from": "S2", "event": "GotoS3", "to": "S3", "action": "GotoS3" },
    { "id": "t500", "from": "S3", "event": "GotoS1", "to": "S1", "action": "GotoS1" }
  ]
}`

const examplePermissions = `{
  "roleA": {
    "include": [],
    "exclude": [
      "/aa/bb/cc/action?op=GotoS2"
    ]
  },
  "roleB": {
    "include": [],
    "exclude": []
  }
}`

export default {
  name: 'StateMachinePlayground',

  components: {
    StateGraph: () => import(/* webpackChunkName: "state-graph" */'@/agilis/components/Graph/StateGraph/StateGraph.vue')
  },

  data () {
    return {
      advancedMode: false,
      fsm: JSON.parse(example),
      fsmAsString: example,
      invalidJSON: false,
      permissions: JSON.parse(examplePermissions),
      premissionsAsString: examplePermissions,
      invalidJSONPermissions: false,
      currentRole: 'roleA',
      trackerType: '',
      openDialog: false,
      dialogFormModel: {
        app: null,
        trackerTypeId: null
      },
      rerenderCounter: 0
    }
  },

  computed: {
    hash () {
      return this.rerenderCounter + this.currentRole // TODO: async computed?
    },
    roles () {
      try {
        const roles = Object.keys(this.permissions)

        return roles
          .filter((r) => {
            try {
              if (this.trackerType) { // on trackerType defined, filter only roles with access to the trackerType
                const include = this.permissions[r].include
                const regexAccessTracker = new RegExp(`/${this.trackerType}$`)
                return include.find(s => regexAccessTracker.test(s))
              }
            } catch (error) {
              // ...
            }

            return true
          })
          .map(r => ({ text: r, value: r }))
      } catch (e) {
        return []
      }
    },

    dialogForm () {
      const appOptions = Object.entries(Configuration.getApplications())
        .filter(([appId, app]) => !!app.trackerTypes)
        .map(([appId, app]) => ({ text: app.name, id: appId }))

      return {
        formRows: [
          { cols: ['app'] },
          { cols: ['trackerTypeId'] }
        ],
        fields: {
          app: {
            id: 'app',
            type: 'select',
            label: 'Application',
            options: appOptions,
            outValue: 'app',
            outValueExpr: '$id',
            required: true
          },
          trackerTypeId: {
            id: 'trackerTypeId',
            type: 'select',
            label: 'Tracker Type',
            outValue: 'trackerTypeId',
            outValueExpr: '$id',
            required: true,
            propsWhen: {
              disabled: model => !model.app,
              options: model => {
                const app = Configuration.getApplications()[model.app]
                const trackerTypes = app?.trackerTypes || {}
                return Object.entries(trackerTypes)
                  .filter(([trackerTypeId, trackerType]) => !!trackerType.stateMachine)
                  .map(([trackerTypeId, trackerType]) => ({ id: trackerTypeId, text: trackerType.name }))
              }
            }
          }
        }
      }
    }
  },

  watch: {
    fsmAsString (newValue, prevValue) {
      if (newValue === prevValue) return
      this.parseJSON(newValue) // TODO: potrebbe valer la pena di debouncare questa chiamata in caso di problemi di performance
      this.rerenderCounter++
    },
    premissionsAsString (newValue, prevValue) {
      if (newValue === prevValue) return
      this.parseJSONPermissions(newValue) // TODO: potrebbe valer la pena di debouncare questa chiamata in caso di problemi di performance
      this.rerenderCounter++
    },
    openDialog () {
      this.dialogFormModel = { app: null, trackerTypeId: null }
    }
  },

  methods: {
    parseJSON (newValue) {
      try {
        this.fsm = JSON.parse(newValue)
        this.invalidJSON = false
      } catch (e) {
        this.invalidJSON = true
      }
    },
    parseJSONPermissions (newValue) {
      try {
        this.permissions = JSON.parse(newValue)
        this.invalidJSONPermissions = false
      } catch (e) {
        this.invalidJSONPermissions = true
      }
    },
    loadFromConfig () {
      const { app, trackerTypeId } = this.dialogFormModel

      // Read config
      const apps = Configuration.getApplications()
      const roles = apps[app].roles
      const trackerType = apps[app].trackerTypes[trackerTypeId]
      const fsm = apps[app].stateMachines[trackerType.stateMachine]

      // Apply variables
      this.premissionsAsString = JSON.stringify(roles, null, 2)
      this.fsmAsString = JSON.stringify(fsm, null, 2)
      this.trackerType = trackerTypeId

      // Close dialog
      this.openDialog = false
    },
    userCanAccessAction (actionId) {
      try {
        const exclude = this.permissions[this.currentRole].exclude
        if (Array.isArray(exclude)) {
          // Exclude the actions
          const exlcudeActionsPermission = `/action$`
          const regexFeature = this.trackerType ? new RegExp(`/${this.trackerType}${exlcudeActionsPermission}`) : new RegExp(exlcudeActionsPermission)

          // Exclude the single actionId
          const exlcudeSingleActionPermission = `/action\\?op=${actionId}$`
          const regexActionId = this.trackerType ? new RegExp(`/${this.trackerType}${exlcudeSingleActionPermission}`) : new RegExp(exlcudeSingleActionPermission)
          return !exclude.find(s => (regexActionId.test(s) || regexFeature.test(s)))
        }
      } catch (error) {
        // ...
      }
      return true
    }
  }
}
